<template>
  <div class="lens-experiment">
    <h1 class="title">Построение изображения в тонкой линзе</h1>
    <p class="subtitle">
      Интерактивная диаграмма для научных статей и учебных пособий по геометрической оптике
    </p>

    <div class="main-layout">
      <!-- SVG Canvas -->
      <div class="canvas-wrap">
        <svg
          :viewBox="`0 0 ${canvasWidth} ${canvasHeight}`"
          class="optical-svg"
          preserveAspectRatio="xMidYMid meet"
        >
          <!-- Определения: стрелки, маркеры -->
          <defs>
            <marker id="arrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill="#333" />
            </marker>
            <marker id="arrowStart" markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M9,0 L9,6 L0,3 z" fill="#333" />
            </marker>
          </defs>

          <!-- Фон -->
          <rect x="0" y="0" :width="canvasWidth" :height="canvasHeight" fill="#fafafa" />

          <!-- Координатная сетка -->
          <g class="grid">
            <line v-for="x in gridX" :key="'gx'+x" :x1="x" y1="0" :x2="x" :y2="canvasHeight" stroke="#e5e5e5" stroke-width="1" />
            <line v-for="y in gridY" :key="'gy'+y" x1="0" :y1="y" :x2="canvasWidth" :y2="y" stroke="#e5e5e5" stroke-width="1" />
          </g>

          <!-- Главная оптическая ось -->
          <line
            :x1="0" :y1="yMid" :x2="canvasWidth" :y2="yMid"
            stroke="#222" stroke-width="1.5"
            marker-end="url(#arrow)" marker-start="url(#arrowStart)"
          />
          <text :x="canvasWidth - 15" :y="yMid - 8" class="axis-label">x</text>

          <!-- Центр линзы O -->
          <circle :cx="lensX" :cy="yMid" r="3" fill="#000" />
          <text :x="lensX + 6" :y="yMid + 18" class="label-bold">O</text>

          <!-- Фокусы -->
          <g v-if="focalLength !== 0">
            <circle :cx="lensX - focalLength" :cy="yMid" r="4" :fill="focalLength > 0 ? '#c00' : '#888'" />
            <text :x="lensX - focalLength" :y="yMid + 22" text-anchor="middle" class="label">
              {{ focalLength > 0 ? 'F' : "F'" }}
            </text>
            <circle :cx="lensX + focalLength" :cy="yMid" r="4" :fill="focalLength > 0 ? '#c00' : '#888'" />
            <text :x="lensX + focalLength" :y="yMid + 22" text-anchor="middle" class="label">
              {{ focalLength > 0 ? "F'" : 'F' }}
            </text>
          </g>

          <!-- Двойное линзовое стекло (более реалистичное) -->
          <path :d="lensPath" fill="rgba(0, 120, 255, 0.08)" stroke="#0055aa" stroke-width="2" />

          <!-- Объект -->
          <g class="object-arrow">
            <line
              :x1="objectX" :y1="yMid"
              :x2="objectX" :y2="yMid - objectHeight"
              stroke="#2e7d32" stroke-width="3"
            />
            <polygon
              :points="`${objectX},${yMid - objectHeight} ${objectX - 6},${yMid - objectHeight + 12} ${objectX + 6},${yMid - objectHeight + 12}`"
              fill="#2e7d32"
            />
            <text :x="objectX" :y="yMid + 20" text-anchor="middle" class="label-bold">d</text>
            <text :x="objectX - 12" :y="yMid - objectHeight / 2" text-anchor="end" class="label-bold">h</text>
          </g>

          <!-- Изображение -->
          <g v-if="isFinite(imageDistance)" class="image-arrow">
            <line
              :x1="imageX" :y1="yMid"
              :x2="imageX" :y2="yMid - imageHeight"
              :stroke="imageReal ? '#c62828' : '#c62828'"
              :stroke-dasharray="imageReal ? '0' : '6,4'"
              stroke-width="3"
            />
            <polygon
              :points="imageArrowPoints"
              :fill="imageReal ? '#c62828' : '#c62828'"
              :opacity="imageReal ? 1 : 0.7"
            />
            <text :x="imageX" :y="yMid + 20" text-anchor="middle" class="label-bold">
              {{ imageReal ? "d'" : "d'" }}
            </text>
            <text :x="imageX + 12" :y="yMid - imageHeight / 2" text-anchor="start" class="label-bold">
              h'
            </text>
          </g>

          <!-- Лучи -->
          <g class="rays">
            <g v-for="(ray, i) in visibleRays" :key="i">
              <!-- Реальная часть луча -->
              <line
                v-for="(seg, j) in ray.segments" :key="'s'+j"
                :x1="seg.x1" :y1="seg.y1" :x2="seg.x2" :y2="seg.y2"
                :stroke="rayColors[i]"
                stroke-width="1.8"
                fill="none"
              />
              <!-- Продолжение назад (для мнимого изображения) -->
              <line
                v-for="(seg, j) in ray.virtualSegments" :key="'v'+j"
                :x1="seg.x1" :y1="seg.y1" :x2="seg.x2" :y2="seg.y2"
                :stroke="rayColors[i]"
                stroke-width="1.5"
                stroke-dasharray="5,4"
                opacity="0.6"
                fill="none"
              />
            </g>
          </g>

          <!-- Подписи лучей -->
          <g class="ray-labels">
            <text :x="rayLabels[0].x" :y="rayLabels[0].y" class="ray-label" fill="#e65100">
              ① параллельно оси
            </text>
            <text :x="rayLabels[1].x" :y="rayLabels[1].y" class="ray-label" fill="#2e7d32">
              ② через центр O
            </text>
            <text :x="rayLabels[2].x" :y="rayLabels[2].y" class="ray-label" fill="#1565c0">
              ③ через фокус
            </text>
          </g>
        </svg>
      </div>

      <!-- Боковая панель -->
      <div class="side-panel">
        <!-- Параметры -->
        <div class="panel-block">
          <h3>Параметры эксперимента</h3>

          <div class="control-group">
            <label>Тип линзы</label>
            <div class="toggle-row">
              <button
                :class="['toggle-btn', { active: focalLength > 0 }]"
                @click="focalLength = Math.abs(focalLength)"
              >
                Собирающая (+)
              </button>
              <button
                :class="['toggle-btn', { active: focalLength < 0 }]"
                @click="focalLength = -Math.abs(focalLength)"
              >
                Рассеивающая (−)
              </button>
            </div>
          </div>

          <div class="control-group">
            <label>Фокусное расстояние f, мм</label>
            <input
              v-model.number="focalLength"
              type="range"
              min="-300"
              max="300"
              step="5"
              class="slider"
            />
            <div class="value-row">
              <input v-model.number="focalLength" type="number" class="num-input" step="5" />
              <span class="unit">мм</span>
            </div>
          </div>

          <div class="control-group">
            <label>Расстояние до объекта d, мм</label>
            <input
              v-model.number="objectDist"
              type="range"
              min="50"
              max="700"
              step="5"
              class="slider"
            />
            <div class="value-row">
              <input v-model.number="objectDist" type="number" class="num-input" step="5" />
              <span class="unit">мм</span>
            </div>
          </div>

          <div class="control-group">
            <label>Высота объекта h, мм</label>
            <input
              v-model.number="objectHeight"
              type="range"
              min="20"
              max="150"
              step="5"
              class="slider"
            />
            <div class="value-row">
              <input v-model.number="objectHeight" type="number" class="num-input" step="5" />
              <span class="unit">мм</span>
            </div>
          </div>

          <button class="reset-btn" @click="resetParams">↺ Сбросить значения</button>
        </div>

        <!-- Расчёты -->
        <div class="panel-block formulas">
          <h3>Расчётные данные</h3>

          <div class="formula-row">
            <span class="formula-name">Формула линзы:</span>
            <span class="formula-math">1/f = 1/d + 1/d'</span>
          </div>

          <div class="data-grid">
            <div class="data-item">
              <span class="data-label">f</span>
              <span class="data-value">{{ fmt(focalLength) }} мм</span>
            </div>
            <div class="data-item">
              <span class="data-label">d</span>
              <span class="data-value">{{ fmt(objectDist) }} мм</span>
            </div>
            <div class="data-item">
              <span class="data-label">d'</span>
              <span class="data-value" :class="{ virtual: !imageReal }">
                {{ fmt(imageDistance) }} мм
              </span>
            </div>
            <div class="data-item">
              <span class="data-label">Γ</span>
              <span class="data-value">{{ fmt(gamma, 3) }}</span>
            </div>
            <div class="data-item">
              <span class="data-label">h'</span>
              <span class="data-value">{{ fmt(imageHeight) }} мм</span>
            </div>
          </div>

          <div class="properties">
            <div class="prop-badge" :class="{ real: imageReal, virtual: !imageReal }">
              {{ imageReal ? 'Действительное' : 'Мнимое' }}
            </div>
            <div class="prop-badge" :class="{ upright: gamma > 0, inverted: gamma < 0 }">
              {{ gamma > 0 ? 'Прямое' : 'Перевёрнутое' }}
            </div>
            <div class="prop-badge" :class="{ enlarged: Math.abs(gamma) > 1, reduced: Math.abs(gamma) < 1 }">
              {{ Math.abs(gamma) > 1 ? 'Увеличенное' : Math.abs(gamma) < 1 ? 'Уменьшенное' : 'Равное' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

/* ==================== КОНФИГУРАЦИЯ ==================== */
const canvasWidth = 1000
const canvasHeight = 600
const yMid = canvasHeight / 2
const lensX = 500

/* ==================== ПАРАМЕТРЫ ==================== */
const focalLength = ref(150)   // мм, может быть отрицательным
const objectDist = ref(300)    // мм
const objectHeight = ref(80)   // мм

/* ==================== СЕТКА ==================== */
const gridX = computed(() => {
  const arr = []
  for (let x = 0; x <= canvasWidth; x += 50) arr.push(x)
  return arr
})
const gridY = computed(() => {
  const arr = []
  for (let y = 0; y <= canvasHeight; y += 50) arr.push(y)
  return arr
})

/* ==================== ГЕОМЕТРИЯ ЛИНЗЫ ==================== */
const lensCurvature = 120
const lensThickness = 16

const lensPath = computed(() => {
  const top = 80
  const bottom = canvasHeight - 80
  const left = lensX - lensThickness / 2
  const right = lensX + lensThickness / 2

  // Рисуем симметричную двояковыпуклую / двояковогнутую линзу
  const c = lensCurvature
  return `
    M ${left} ${yMid - c}
    Q ${left} ${top} ${lensX} ${top}
    Q ${right} ${top} ${right} ${yMid - c}
    L ${right} ${yMid + c}
    Q ${right} ${bottom} ${lensX} ${bottom}
    Q ${left} ${bottom} ${left} ${yMid + c}
    Z
  `
})

/* ==================== ВЫЧИСЛЕНИЯ ИЗОБРАЖЕНИЯ ==================== */
const objectX = computed(() => lensX - objectDist.value)

const imageDistance = computed(() => {
  const f = focalLength.value
  const d = objectDist.value
  if (Math.abs(f) < 0.1 || Math.abs(d - f) < 0.1) return Infinity
  return (f * d) / (d - f)
})

const gamma = computed(() => {
  const dPrime = imageDistance.value
  if (!isFinite(dPrime)) return 0
  return -dPrime / objectDist.value
})

const imageHeight = computed(() => gamma.value * objectHeight.value)

const imageX = computed(() => {
  const dPrime = imageDistance.value
  if (!isFinite(dPrime)) return lensX
  return lensX + dPrime
})

const imageReal = computed(() => imageDistance.value > 0)

const imageArrowPoints = computed(() => {
  const x = imageX.value
  const y = yMid - imageHeight.value
  if (imageHeight.value >= 0) {
    // Навершие вверх
    return `${x},${y} ${x - 6},${y + 12} ${x + 6},${y + 12}`
  }
  // Навершие вниз (перевёрнутое)
  return `${x},${y} ${x - 6},${y - 12} ${x + 6},${y - 12}`
})

/* ==================== ЛУЧИ ==================== */
const rayColors = ['#e65100', '#2e7d32', '#1565c0']

function yAt(x, x1, y1, x2, y2) {
  if (Math.abs(x2 - x1) < 0.1) return y1
  return y1 + (y2 - y1) * (x - x1) / (x2 - x1)
}

const visibleRays = computed(() => {
  const f = focalLength.value
  const h = objectHeight.value
  const ox = objectX.value
  const oy = yMid - h
  const imgX = imageX.value
  const imgY = yMid - imageHeight.value
  const isReal = imageReal.value
  const rightBound = canvasWidth

  const rays = []

  if (!isFinite(imageDistance.value)) {
    // Параллельные лучи (d = f для собирающей)
    rays.push({
      segments: [
        { x1: ox, y1: oy, x2: lensX, y2: oy },
        { x1: lensX, y1: oy, x2: rightBound, y2: oy }
      ],
      virtualSegments: []
    })
    rays.push({
      segments: [
        { x1: ox, y1: oy, x2: lensX, y2: yMid },
        { x1: lensX, y1: yMid, x2: rightBound, y2: yMid }
      ],
      virtualSegments: []
    })
    rays.push({
      segments: [
        { x1: ox, y1: oy, x2: lensX, y2: yAt(lensX, ox, oy, lensX - f, yMid) },
        { x1: lensX, y1: yAt(lensX, ox, oy, lensX - f, yMid), x2: rightBound, y2: yAt(rightBound, lensX, yAt(lensX, ox, oy, lensX - f, yMid), lensX - f, yMid) }
      ],
      virtualSegments: []
    })
    return rays
  }

  // === ЛУЧ 1: параллельно оси → через/от F' ===
  const fPrimeX = lensX + f
  const yAfter1 = yAt(rightBound, lensX, oy, fPrimeX, yMid)
  if (isReal) {
    rays.push({
      segments: [
        { x1: ox, y1: oy, x2: lensX, y2: oy },
        { x1: lensX, y1: oy, x2: rightBound, y2: yAfter1 }
      ],
      virtualSegments: []
    })
  } else {
    // Мнимое: сплошная вперёд, пунктир назад к imgX
    rays.push({
      segments: [
        { x1: ox, y1: oy, x2: lensX, y2: oy },
        { x1: lensX, y1: oy, x2: rightBound, y2: yAfter1 }
      ],
      virtualSegments: [
        { x1: imgX, y1: imgY, x2: lensX, y2: oy }
      ]
    })
  }

  // === ЛУЧ 2: через центр O ===
  const yAfter2 = yAt(rightBound, ox, oy, lensX, yMid)
  if (isReal) {
    rays.push({
      segments: [
        { x1: ox, y1: oy, x2: lensX, y2: yMid },
        { x1: lensX, y1: yMid, x2: rightBound, y2: yAfter2 }
      ],
      virtualSegments: []
    })
  } else {
    rays.push({
      segments: [
        { x1: ox, y1: oy, x2: lensX, y2: yMid },
        { x1: lensX, y1: yMid, x2: rightBound, y2: yAfter2 }
      ],
      virtualSegments: [
        { x1: imgX, y1: imgY, x2: lensX, y2: yMid }
      ]
    })
  }

  // === ЛУЧ 3: через F (собирающая) или к F' (рассеивающая) ===
  // Для собирающей: луч от объекта через F=lensX-f (слева), после линзы параллельно оси.
  // Для рассеивающей: луч от объекта к F'=lensX+f (слева), после линзы параллельно оси.
  const focalTargetX = f > 0 ? lensX - f : lensX + f
  const ray3Valid = f < 0 || ox <= focalTargetX // для собирающей объект должен быть левее F

  if (ray3Valid) {
    const yAtLens3 = yAt(lensX, ox, oy, focalTargetX, yMid)
    const seg3a = { x1: ox, y1: oy, x2: lensX, y2: yAtLens3 }
    const seg3b = { x1: lensX, y1: yAtLens3, x2: rightBound, y2: yAtLens3 }

    if (isReal) {
      rays.push({
        segments: [seg3a, seg3b],
        virtualSegments: []
      })
    } else {
      rays.push({
        segments: [seg3a, seg3b],
        virtualSegments: [
          { x1: imgX, y1: imgY, x2: lensX, y2: yAtLens3 }
        ]
      })
    }
  } else {
    // При d < f для собирающей луч 3 не строится классически — дублируем луч 1
    // или рисуем альтернативный: от объекта к линзе, после как будто от F'
    const yAfterAlt = yAt(rightBound, lensX, oy, fPrimeX, yMid)
    rays.push({
      segments: [
        { x1: ox, y1: oy, x2: lensX, y2: oy },
        { x1: lensX, y1: oy, x2: rightBound, y2: yAfterAlt }
      ],
      virtualSegments: [
        { x1: imgX, y1: imgY, x2: lensX, y2: oy }
      ]
    })
  }

  return rays
})

const rayLabels = computed(() => {
  return [
    { x: canvasWidth - 200, y: yMid - objectHeight.value - 25 },
    { x: canvasWidth - 200, y: yMid + 35 },
    { x: canvasWidth - 200, y: yMid + objectHeight.value + 45 }
  ]
})

/* ==================== УТИЛИТЫ ==================== */
function fmt(val, digits = 1) {
  if (!isFinite(val)) return '∞'
  return val.toFixed(digits)
}

function resetParams() {
  focalLength.value = 150
  objectDist.value = 300
  objectHeight.value = 80
}
</script>

<style scoped>
.lens-experiment {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  font-family: 'Segoe UI', system-ui, sans-serif;
  color: #1a1a1a;
  background: #fff;
}

.title {
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #111;
}

.subtitle {
  font-size: 14px;
  color: #555;
  margin: 0 0 20px;
}

.main-layout {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
  align-items: start;
}

@media (max-width: 1100px) {
  .main-layout {
    grid-template-columns: 1fr;
  }
}

/* SVG */
.canvas-wrap {
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
}

.optical-svg {
  width: 100%;
  height: auto;
  display: block;
}

.axis-label {
  font-size: 14px;
  fill: #333;
  font-style: italic;
}

.label {
  font-size: 13px;
  fill: #333;
  font-family: 'Times New Roman', serif;
}

.label-bold {
  font-size: 14px;
  fill: #000;
  font-weight: 700;
  font-family: 'Times New Roman', serif;
}

.ray-label {
  font-size: 12px;
  font-family: 'Segoe UI', sans-serif;
  opacity: 0.85;
}

/* Панель */
.side-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.panel-block {
  background: #f7f8fa;
  border: 1px solid #e1e4e8;
  border-radius: 10px;
  padding: 18px;
}

.panel-block h3 {
  margin: 0 0 14px;
  font-size: 15px;
  color: #222;
  border-bottom: 1px solid #e1e4e8;
  padding-bottom: 8px;
}

.control-group {
  margin-bottom: 16px;
}

.control-group label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #444;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.toggle-row {
  display: flex;
  gap: 8px;
}

.toggle-btn {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
}

.toggle-btn.active {
  background: #1f6feb;
  color: #fff;
  border-color: #1f6feb;
}

.slider {
  width: 100%;
  margin-bottom: 6px;
}

.value-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.num-input {
  width: 80px;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  text-align: right;
}

.unit {
  font-size: 12px;
  color: #666;
}

.reset-btn {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
}

.reset-btn:hover {
  background: #f0f0f0;
}

/* Формулы */
.formula-row {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #d0d0d0;
}

.formula-name {
  font-size: 12px;
  color: #555;
}

.formula-math {
  display: block;
  margin-top: 4px;
  font-family: 'Times New Roman', serif;
  font-size: 16px;
  font-style: italic;
  color: #111;
}

.data-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}

.data-item {
  background: #fff;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.data-label {
  font-family: 'Times New Roman', serif;
  font-style: italic;
  font-size: 15px;
  color: #444;
}

.data-value {
  font-size: 14px;
  font-weight: 600;
  color: #111;
  font-variant-numeric: tabular-nums;
}

.data-value.virtual {
  color: #c62828;
}

.properties {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.prop-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
}

.prop-badge.real {
  background: #e8f5e9;
  color: #2e7d32;
  border-color: #a5d6a7;
}

.prop-badge.virtual {
  background: #ffebee;
  color: #c62828;
  border-color: #ef9a9a;
}

.prop-badge.upright {
  background: #e3f2fd;
  color: #1565c0;
  border-color: #90caf9;
}

.prop-badge.inverted {
  background: #fff3e0;
  color: #ef6c00;
  border-color: #ffcc80;
}

.prop-badge.enlarged {
  background: #f3e5f5;
  color: #6a1b9a;
  border-color: #ce93d8;
}

.prop-badge.reduced {
  background: #e0f7fa;
  color: #00838f;
  border-color: #80deea;
}
</style>
